import { PtTree } from "@/shared-components";
import {
  FavoritesProvider,
  FoldersContext,
  useFavoritesContext,
} from "@/contexts";
import { useContext, useEffect, useRef, useState } from "react";
import { FolderContextMenu } from "../FolderContextMenu";
import { DEFAULT_FILTER_KEY } from "@/constants";
import { FoldersService, SearchService, TreeService } from "@/services";
import { TextInputModal } from "@/shared-components";
import {
  getActiveItemFromUrl,
  getTranslation,
  hasPermission,
  showNotification,
} from "@/utils";
import { IDefaultTreeItem, ISearchResult, ITreeItem } from "@/interfaces";

interface IFilterListProps {
  notifyFolderChange: (
    node: ITreeItem | IDefaultTreeItem,
    isUserAction?: boolean,
  ) => void;
  maxNumberOfFavorites: number;
}

export const FolderList = ({
  notifyFolderChange,
  maxNumberOfFavorites,
}: IFilterListProps) => {
  const store = useContext(FoldersContext);
  const {
    loading,
    error,
    scope,
    criteria,
    items,
    selectedItem,
    handleNodeExpand,
    setSelectedItem,
    setCriteria,
    addFirstLevelFolder,
    reloadItems,
    handleServerSideSearch,
    settings,
  } = store!;
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const hasEditPermissions = hasPermission("tests_editor");
  const isStateReadyRef = useRef<boolean>(false);
  const [openNodes, setOpenedNodes] = useState<string[]>([]);
  const { setActiveFavorite } = useFavoritesContext();
  const [, setIsDataReady] = useState<boolean>(false);

  useEffect(() => {
    setOpenedNodes(TreeService.getOpenedNodes(scope));
  }, []);

  useEffect(() => {
    if (selectedItem?.id) {
      setActiveFavorite(selectedItem.id);
    }
  }, [selectedItem?.id]);

  useEffect(() => {
    if (!loading || isStateReadyRef.current) return;

    isStateReadyRef.current = true;
    //Get from url on the first load the
    const urlActiveId = getActiveItemFromUrl(scope);

    if (urlActiveId) {
      const node = SearchService.findFilterItem(items, urlActiveId);
      const openNodes = TreeService.getOpenedNodes(scope);
      if (node && node.serverObject?.ancestry) {
        const ancestries = node.serverObject.ancestry.split("/") || [];
        openNodes.push(...ancestries);
      }

      const uniqueStringsSet: Set<string> = new Set(openNodes);
      TreeService.setOpenedNodes(scope, Array.from(uniqueStringsSet));
      node && notifyFolderChange(node, false);
    } else if (settings?.disableBookmarks) {
      // Without bookmarks (e.g. in a modal) the url has no folder param, but the
      // tree still highlights the stored active folder - load its entities too
      const activeNode = TreeService.getActiveNode(scope);
      const node =
        activeNode?.id &&
        (SearchService.findFilterItem(items, activeNode.id) || activeNode);
      node && notifyFolderChange(node, false);
    }
    setIsDataReady(true);
  }, [items, scope]);

  const handleTreeItemClick = (
    node: ITreeItem | IDefaultTreeItem,
    isUserAction: boolean = true,
  ) => {
    if (node?.id === DEFAULT_FILTER_KEY) {
      setActiveFavorite(null);
    }
    TreeService.setActiveNode(scope, node);
    setSelectedItem(node?.id !== DEFAULT_FILTER_KEY ? node : undefined);
    notifyFolderChange(node, isUserAction);
  };

  const handleAddFolder = async (newFolderName: string) => {
    try {
      mixpanel.track("folder-tree", {
        Action: "add-folder",
      });
      setShowAddModal(false);
      const newTreeItem = await addFirstLevelFolder(newFolderName);
      items.push(newTreeItem);
      const newState = items.sort((a, b) => a.name.localeCompare(b.name));
      reloadItems(newState);

      if (newTreeItem?.id) {
        window.history.replaceState(
          {},
          "",
          `${window.location.origin}${window.location.pathname}?folder=${newTreeItem.id}`,
        );
      }
    } catch (e: any) {
      console.error(`FolderList->handleAddFolder`, e);
      showNotification("error", e?.message || "Failed to save folder");
    }
  };

  const handleNodeRename = async (id: string, value: string): Promise<void> => {
    await FoldersService.rename(scope.projectId, id, value, items);
  };

  const handleSearch = async (
    criteria: string,
  ): Promise<ISearchResult | undefined> => {
    return await handleServerSideSearch(criteria);
  };

  return (
    <FavoritesProvider
      scope={scope}
      tree={items || []}
      maxNumberOfFavorites={maxNumberOfFavorites}
    >
      <PtTree
        data={items || []}
        scope={scope}
        initOpenNodes={openNodes || []}
        settings={settings}
        contextMenu={({ data, setIsEditable }: any) => (
          <FolderContextMenu
            setIsEditable={setIsEditable}
            items={items}
            key={data.id}
            data={data}
            hasEditPermissions={hasEditPermissions}
          />
        )}
        criteria={criteria}
        setCriteria={setCriteria}
        selectedItem={selectedItem}
        loading={loading}
        error={error}
        notifyTreeNodeOpen={handleNodeExpand}
        hasEditPermissions={hasEditPermissions}
        handleTreeItemClick={handleTreeItemClick}
        handleAddItem={() => setShowAddModal(true)}
        handleNodeRename={handleNodeRename}
        handleSearch={handleSearch}
      />

      <TextInputModal
        show={showAddModal}
        title={getTranslation(
          "filter-tree.modal.add-folder.title",
          "Add Folder",
        )}
        placeholder={getTranslation(
          "filter-tree.modal.add-folder.placeholder",
          "New Folder Name",
        )}
        onYes={handleAddFolder}
        onNo={() => setShowAddModal(false)}
        yesLabel={getTranslation("generic.button.save", "Save")}
        noLabel={getTranslation("generic.button.cancel", "Cancel")}
      />
    </FavoritesProvider>
  );
};
